import { create } from 'zustand';

import type { ActivityCardSlice, ActivityDraft } from '@/chat/activity';
import { emptyDraft } from '@/chat/activity';

/**
 * The armed activity's in-progress answer.
 *
 * The interactive card lives in the thread and the Reset / Check buttons live
 * in the footer — two different subtrees of the chat, with the reducer in
 * between. Rather than thread a draft through the whole chat, the two ends
 * share this store: exactly one activity is ever armed, so a single slot is
 * the whole state.
 *
 * The footer owns arming (it is the component the reducer hands the prompt to)
 * and commits on Check; the card reads `active` to know whether it is live and
 * `submitted` to redraw itself once frozen.
 */

export type ActivityDraftStore = {
  /** The armed activity, or `null` when no activity is waiting for an answer. */
  active: { stepId: string; attempt: number; draft: ActivityDraft } | null;
  /** The last checked draft per step, so a frozen card still shows the answer given. */
  submitted: Record<string, ActivityDraft>;
  /** Arms a step. Idempotent for the same attempt, so an effect can call it freely. */
  arm: (stepId: string, attempt: number, slice: ActivityCardSlice) => void;
  /** Clears the armed draft back to empty — the Reset button. */
  reset: (slice: ActivityCardSlice) => void;
  update: (draft: ActivityDraft) => void;
  /** Records the draft against its step and disarms: the card is now a record. */
  commit: () => void;
};

export const useActivityDraft = create<ActivityDraftStore>()((set) => ({
  active: null,
  submitted: {},

  arm: (stepId, attempt, slice) =>
    set((state) =>
      state.active?.stepId === stepId && state.active.attempt === attempt
        ? state
        : { active: { stepId, attempt, draft: emptyDraft(slice) } },
    ),

  reset: (slice) =>
    set((state) => (state.active === null ? state : { active: { ...state.active, draft: emptyDraft(slice) } })),

  update: (draft) => set((state) => (state.active === null ? state : { active: { ...state.active, draft } })),

  commit: () =>
    set((state) =>
      state.active === null
        ? state
        : { active: null, submitted: { ...state.submitted, [state.active.stepId]: state.active.draft } },
    ),
}));

/** The draft to draw for one card: the live one while armed, else what was submitted. */
export function useCardDraft(stepId: string): { draft: ActivityDraft | null; armed: boolean; attempt: number } {
  const active = useActivityDraft((state) => state.active);
  const submitted = useActivityDraft((state) => state.submitted[stepId]);

  return active?.stepId === stepId
    ? { draft: active.draft, armed: true, attempt: active.attempt }
    : { draft: submitted ?? null, armed: false, attempt: 1 };
}
